import {
  CLEARANCE_MIN,
  buildCapsules,
  clearanceAt,
  neighbourPairs,
  type Capsule,
} from './clearance'
import { SIDES, type Side } from './gait'
import type { PlaneKey } from './joints'
import {
  poseAt,
  restAngle,
  soleHeight,
  type AngleLookup,
  type Pose,
  type Skeleton,
} from './kinematics'
import { planeLimits, type HumanoidModel, type PlaneLimit } from './model'
import type { Vec3 } from './vec'

/**
 * Grenzlagen ohne Gangzyklus — was die Maschine KÖNNTE, nicht was sie gerade tut.
 *
 * Der Unterschied zur Freigangsprüfung in `clearance.ts` ist der Bezug. Dort
 * bewegt sich die Maschine auf einer Bahn, und gefragt wird, ob sie sich dabei
 * ins Gehege kommt. Hier gibt es keine Bahn: gefragt wird, wie weit eine Achse
 * überhaupt fahren darf und welche Punkte eine Kette erreicht. Das ist die
 * Frage des Konstrukteurs und nicht die des Bewegungsplaners.
 *
 * WARUM DAS EINE EIGENE RECHNUNG IST UND KEINE AUSWERTUNG DER ANDEREN. Eine
 * Bahn besucht immer nur einen dünnen Faden im Winkelraum. Dass sie kollisionsfrei
 * ist, sagt nichts darüber, wie weit daneben noch Platz ist — und genau das ist
 * die Reserve, nach der man fragt, bevor man eine Bewegung ändert.
 */

/* -------------------------------------------------------------------------
   Achsgrenzen
   ------------------------------------------------------------------------- */

/**
 * Wie weit EINE Achse fahren darf, ohne dass sich zwei Hüllkörper berühren.
 *
 * DIE EINSCHRÄNKUNG, DIE MAN KENNEN MUSS: gefahren wird nur diese eine Achse,
 * alle anderen stehen in der Grundstellung. Das ist die ISOLIERTE Reserve. Zwei
 * Achsen zusammen können deutlich früher anschlagen — der Ellbogen darf allein
 * weit beugen und trifft dennoch die Taille, sobald die Schulter mitgeht.
 *
 * Sie trotzdem so zu rechnen ist die richtige Entscheidung, weil die Alternative
 * keine ist: der vollständige Freiheitsgradraum hat bei elf Gelenken zwanzig
 * Achsen, und ihn abzutasten kostet mehr Zeit, als eine Konstruktionssitzung
 * hat. Die Paaranalyse über den Zyklus beantwortet die andere Hälfte der Frage.
 */
export interface AxisTravel {
  joint: string
  plane: PlaneKey
  side: Side
  /** Die Ruhelage dieser Achse, Grad — der Ausgangspunkt der Suche. */
  rest: number
  /** Untere kollisionsfreie Grenze, Grad. */
  lo: number
  /** Obere kollisionsfreie Grenze, Grad. */
  hi: number
  /** Schlägt die Achse unten am Modul an statt an einem Hüllkörper? */
  loFromModule: boolean
  /** Und oben? */
  hiFromModule: boolean
  /** Der Körper, der die Fahrt nach unten begrenzt — leer, wenn das Modul es tut. */
  loBlocker: string | null
  hiBlocker: string | null
}

/**
 * Alle Achsen und die Grundstellung, aus der heraus gesucht wurde.
 *
 * Der Freigang in der Grundstellung steht EINMAL und nicht an jeder Achse: es
 * ist eine Eigenschaft der Stellung und nicht der Achse. An jeder Zeile
 * wiederholt sähe er wie eine Messgrösse je Achse aus, die er nicht ist.
 */
export interface TravelResult {
  travels: AxisTravel[]
  /** Kleinster Abstand zweier Hüllkörper in der Grundstellung, m. */
  restClearance: number
  /** Welches Paar dort am engsten steht. */
  restPair: string | null
}

/** Schrittweite der Suche in Grad — grob, danach wird halbiert. */
const SWEEP_STEP = 4

/** Wie oft nach dem groben Treffer halbiert wird. Vier Schritte ⇒ 0,25°. */
const REFINE = 4

/**
 * Sucht für jede Achse den kollisionsfreien Bereich.
 *
 * Von der Ruhelage aus in beide Richtungen, bis der Abstand unter den
 * geforderten Freigang fällt oder die Modulgrenze erreicht ist. Der Übergang
 * wird danach durch Halbieren eingegrenzt: die grobe Schrittweite findet ihn,
 * die Halbierung sagt, wo genau er liegt.
 *
 * DIE SUCHE GEHT VON DER RUHELAGE AUS UND NICHT VOM RAND. Eine Achse kann in
 * ihrem Bereich mehrere kollisionsfreie Inseln haben — vom Rand her gesucht
 * fände man die falsche. Von der Ruhelage her findet man die, in der die
 * Maschine steht, und nur die ist zusammenhängend erreichbar.
 */
export function axisTravels(
  model: HumanoidModel,
  skeleton: Skeleton,
  minimum: number = CLEARANCE_MIN,
  /* Stillgelegte Freiheiten haben keinen Bereich mehr — sie fallen aus der
     Prüfung heraus statt mit einem Bereich zu erscheinen, den es nicht gibt. */
  off: Iterable<string> = [],
): TravelResult {
  const capsules = buildCapsules(skeleton, model.scale)
  const skip = neighbourPairs()
  const limits = planeLimits(model, off)
  const out: AxisTravel[] = []

  /* Die Beckenhöhe ist beliebig — die Prüfung vergleicht Körper untereinander
     und nicht gegen den Boden. */
  const place = { x: 0, y: skeleton.pelvisRest, z: 0 }

  const probe = (
    joint: string,
    plane: PlaneKey,
    side: Side,
    angle: number,
  ): { distance: number; a?: Capsule; b?: Capsule } => {
    /* Alle anderen Achsen stehen in der GRUNDSTELLUNG und nicht auf null. Auf
       null hängt die Hand im Becken, und dann meldet jede Achse null Fahrweg —
       nicht weil sie steif wäre, sondern weil die Ausgangsstellung eine ist,
       die die Maschine nie einnimmt. */
    const lookup: AngleLookup = (j, p, s) =>
      j === joint && p === plane && s === side ? angle : restAngle(j, p, s)
    return clearanceAt(poseAt(skeleton, lookup, place), capsules, skip)
  }

  for (const joint of Object.keys(limits)) {
    for (const plane of Object.keys(limits[joint]) as PlaneKey[]) {
      const limit = limits[joint][plane]
      if (!limit) continue
      for (const side of SIDES) {
        /*
          GEFAHREN WIRD AB DER RUHELAGE DIESER ACHSE und nicht ab null. Für die
          meisten Achsen ist das dasselbe; für den Ellbogen nicht, und dort
          entscheidet es alles: bei null steht er durchgestreckt, die Hand liegt
          im Becken, und die Suche meldete null Fahrweg für eine Achse, die
          140° kann.
        */
        const from = restAngle(joint, plane, side)
        const up = sweep(probe, joint, plane, side, limit, minimum, +1, from)
        const down = sweep(probe, joint, plane, side, limit, minimum, -1, from)
        out.push({
          joint,
          plane,
          side,
          rest: from,
          lo: down.angle,
          hi: up.angle,
          loFromModule: down.atModule,
          hiFromModule: up.atModule,
          loBlocker: down.blocker,
          hiBlocker: up.blocker,
        })
      }
    }
  }

  const rest = clearanceAt(poseAt(skeleton, restAngle, place), capsules, skip)
  return { travels: out, restClearance: rest.distance, restPair: collision(rest) }
}

function sweep(
  probe: (
    joint: string,
    plane: PlaneKey,
    side: Side,
    angle: number,
  ) => { distance: number; a?: Capsule; b?: Capsule },
  joint: string,
  plane: PlaneKey,
  side: Side,
  limit: PlaneLimit,
  minimum: number,
  direction: 1 | -1,
  from: number,
): { angle: number; atModule: boolean; blocker: string | null } {
  const bound = direction > 0 ? limit.hi : limit.lo
  /* Die Ruhelage liegt am Rand oder ausserhalb des Moduls — dann gibt es in
     dieser Richtung nichts zu fahren. */
  if (direction > 0 ? bound <= from : bound >= from) {
    return { angle: from, atModule: true, blocker: null }
  }

  let last = from
  for (let step = SWEEP_STEP; ; step += SWEEP_STEP) {
    const angle = direction > 0 ? Math.min(bound, from + step) : Math.max(bound, from - step)
    const hit = probe(joint, plane, side, angle)

    if (hit.distance < minimum) {
      /* Zwischen `last` (frei) und `angle` (blockiert) halbieren. */
      let free = last
      let blocked = angle
      let blocker = collision(hit)
      for (let i = 0; i < REFINE; i++) {
        const mid = (free + blocked) / 2
        const test = probe(joint, plane, side, mid)
        if (test.distance < minimum) {
          blocked = mid
          blocker = collision(test)
        } else {
          free = mid
        }
      }
      return { angle: free, atModule: false, blocker }
    }

    last = angle
    if (angle === bound) return { angle: bound, atModule: true, blocker: null }
  }
}

const collision = (hit: { a?: Capsule; b?: Capsule }): string | null =>
  hit.a && hit.b ? `${hit.a.label} ↔ ${hit.b.label}` : null

/* -------------------------------------------------------------------------
   Drei Grenzen je Achse
   ------------------------------------------------------------------------- */

/** Woher eine Grenze kommt. */
export type LimitOrigin = 'gefordert' | 'modulseitig' | 'kollisionsfrei'

export interface LimitBand {
  lo: number
  hi: number
  origin: LimitOrigin
  note: string
}

/**
 * Was eine Achse darf — aus drei Richtungen betrachtet.
 *
 * GEFORDERT ist keine Grenze, sondern ein Anspruch: der Bereich, den die
 * gerechnete Bewegung tatsächlich braucht. Er steht daneben, weil erst der
 * Vergleich die Auskunft ergibt. Eine Achse, die 40° kann und 38° braucht, ist
 * knapp; eine, die 40° kann und 6° braucht, ist überdimensioniert. Beides sieht
 * man nur nebeneinander.
 *
 * MODULSEITIG ist, was die Winkelfreiheit hergibt — die Zahl aus dem Datenblatt
 * des Antriebsmoduls.
 *
 * KOLLISIONSFREI ist, wie weit die Achse fahren darf, bevor sich zwei
 * Hüllkörper berühren. Das ist die Grenze, die niemand ins Datenblatt schreibt
 * und die trotzdem gilt.
 *
 * DIE KLEINSTE GEWINNT — aber nur unter den beiden ECHTEN Grenzen. Die
 * geforderte Bewegung begrenzt nichts; sie wird begrenzt. Wer sie in das
 * Minimum hineinnimmt, bekommt eine Achse, die genau so viel kann, wie sie
 * gerade tut, und die Frage nach der Reserve ist nicht mehr stellbar.
 */
export interface AxisLimits {
  joint: string
  plane: PlaneKey
  side: Side
  required: LimitBand | null
  module: LimitBand
  free: LimitBand
  /** Der Bereich, der wirklich gilt: das Kleinere aus Modul und Freigang. */
  effective: LimitBand
  /**
   * Was den Bereich an jedem Ende schliesst.
   *
   * NICHT ÜBER DEN ZAHLENVERGLEICH BESTIMMT, und das ist der Punkt. Wo eine
   * Achse nirgends gegen einen Körper fährt, ENDET sie auf der Modulgrenze —
   * die beiden Zahlen sind dann gleich, und ein Vergleich meldete „der Freigang
   * bindet auch". Er bindet nicht; er hat nichts gefunden. Die Suche weiss das
   * und sagt es, der Vergleich kann es nicht wissen.
   */
  binding: { lo: LimitOrigin; hi: LimitOrigin }
  /**
   * Wie weit die geforderte Bewegung den geltenden Bereich ausschöpft, 0 … 1.
   *
   * Über 1 heisst: die Bewegung verlangt mehr, als die Achse kann. `null`, wenn
   * keine Bewegung vorliegt.
   */
  use: number | null
  /** Die Bewegung geht über den geltenden Bereich hinaus. */
  exceeded: boolean
}

/**
 * Toleranz beim Vergleich von Winkeln, Grad.
 *
 * Nur noch für die Überschreitungsmeldung: eine Bewegung, die die Grenze um ein
 * Zehntelgrad reisst, reisst sie nicht — das ist die Auflösung der Suche und
 * nicht ein Entwurfsfehler.
 */
const SAME = 0.5

/**
 * Legt die drei Grenzen nebeneinander.
 *
 * `demand` liefert den Bereich, den die Bewegung braucht — oder `null`, wo es
 * keine Bewegungsvorgabe gibt. Die Funktion holt ihn sich NICHT selbst: sie
 * soll auch ohne Gang aufrufbar sein, etwa auf der Modellseite, wo man Grenzen
 * vergleicht, bevor man eine Bewegung gewählt hat.
 */
export function axisLimits(
  model: HumanoidModel,
  travels: AxisTravel[],
  demand: (joint: string, plane: PlaneKey, side: Side) => { lo: number; hi: number } | null,
  off: Iterable<string> = [],
): AxisLimits[] {
  const limits = planeLimits(model, off)

  return travels.map((travel) => {
    const limit = limits[travel.joint]?.[travel.plane]
    const module: LimitBand = {
      lo: limit?.lo ?? -180,
      hi: limit?.hi ?? 180,
      origin: 'modulseitig',
      note: limit?.source ?? 'keine Winkelfreiheit hinterlegt',
    }
    const free: LimitBand = {
      lo: travel.lo,
      hi: travel.hi,
      origin: 'kollisionsfrei',
      note: [travel.loBlocker, travel.hiBlocker].filter(Boolean).join(' · ') || 'kein Anschlag',
    }

    const lo = Math.max(module.lo, free.lo)
    const hi = Math.min(module.hi, free.hi)
    const effective: LimitBand = {
      lo,
      hi,
      origin: 'kollisionsfrei',
      note: 'die kleinere aus Modul und Freigang',
    }

    const binding = {
      lo: travel.loFromModule ? ('modulseitig' as const) : ('kollisionsfrei' as const),
      hi: travel.hiFromModule ? ('modulseitig' as const) : ('kollisionsfrei' as const),
    }

    const span = demand(travel.joint, travel.plane, travel.side)
    const required: LimitBand | null = span
      ? { lo: span.lo, hi: span.hi, origin: 'gefordert', note: 'aus der gerechneten Bewegung' }
      : null

    const width = hi - lo
    const use = required && width > 0 ? (required.hi - required.lo) / width : null

    return {
      joint: travel.joint,
      plane: travel.plane,
      side: travel.side,
      required,
      module,
      free,
      effective,
      binding,
      use,
      exceeded: required !== null && (required.lo < lo - SAME || required.hi > hi + SAME),
    }
  })
}

/* -------------------------------------------------------------------------
   Arbeitsraum
   ------------------------------------------------------------------------- */

/** Eine kinematische Kette und die Achsen, über die sie abgetastet wird. */
export interface ChainSpec {
  key: string
  label: string
  /** Der Knoten, dessen erreichbare Lage gesucht wird. */
  tip: 'wrist' | 'ankle' | 'toe'
  axes: { joint: string; plane: PlaneKey }[]
}

export const CHAINS: readonly ChainSpec[] = [
  {
    key: 'arm',
    label: 'Arm bis Handwurzel',
    tip: 'wrist',
    axes: [
      { joint: 'shoulder', plane: 'sag' },
      { joint: 'shoulder', plane: 'fro' },
      { joint: 'shoulder', plane: 'tra' },
      { joint: 'elbow', plane: 'sag' },
    ],
  },
  {
    key: 'leg',
    label: 'Bein bis Sprunggelenk',
    tip: 'ankle',
    axes: [
      { joint: 'hip', plane: 'sag' },
      { joint: 'hip', plane: 'fro' },
      { joint: 'hip', plane: 'tra' },
      { joint: 'knee', plane: 'sag' },
    ],
  },
  {
    key: 'foot',
    label: 'Bein bis Zehenspitze',
    tip: 'toe',
    axes: [
      { joint: 'hip', plane: 'sag' },
      { joint: 'knee', plane: 'sag' },
      { joint: 'ankle', plane: 'sag' },
      { joint: 'ankle', plane: 'fro' },
    ],
  },
]

export interface WorkspacePoint {
  /** Lage relativ zur Beckenmitte, m. */
  p: Vec3
  /** Kollisionsfrei erreichbar? */
  free: boolean
}

export interface Workspace {
  chain: ChainSpec
  side: Side
  points: WorkspacePoint[]
  /** Wie viele der abgetasteten Stellungen kollisionsfrei waren. */
  freeCount: number
  /** Grösste Entfernung von der Beckenmitte, m. */
  reach: number
  /** Rechteckhülle der kollisionsfreien Punkte, m. */
  box: { min: Vec3; max: Vec3 }
  /** Stützstellen je Achse. */
  steps: number
}

/**
 * Tastet den erreichbaren Raum einer Kette ab.
 *
 * VIER ACHSEN MIT `steps` STÜTZSTELLEN SIND `steps⁴` STELLUNGEN. Bei fünf
 * Stützstellen sind das 625, bei sieben schon 2401 — und jede kostet eine
 * vollständige Kollisionsprüfung über alle Körperpaare. Die Zahl ist deshalb
 * ein Argument und keine Konstante: die Oberfläche fängt grob an und verfeinert
 * auf Wunsch.
 *
 * ES ENTSTEHT EINE PUNKTWOLKE UND KEIN VOLUMEN. Das ist Absicht. Eine Hülle um
 * die Wolke zu legen hiesse, über die Zwischenräume etwas zu behaupten, das
 * nicht geprüft wurde — und der Arbeitsraum eines Beines ist nicht konvex.
 * Wer eine Hülle will, bekommt sie über die Rechteckhülle, und die ist ehrlich
 * beschriftet: sie ist eine OBERE Schranke.
 */
export function workspace(
  model: HumanoidModel,
  skeleton: Skeleton,
  chain: ChainSpec,
  side: Side,
  travels: AxisTravel[],
  steps = 5,
): Workspace {
  const capsules = buildCapsules(skeleton, model.scale)
  const skip = neighbourPairs()
  const place = { x: 0, y: skeleton.pelvisRest, z: 0 }

  /* Die Achsen fahren im KOLLISIONSFREIEN Bereich, nicht im modulseitigen: der
     Arbeitsraum soll zeigen, wohin die Maschine wirklich kommt. Wo keine
     Achsgrenze vorliegt, bleibt die Achse stehen. */
  const ranges = chain.axes.map((axis) => {
    const travel = travels.find(
      (t) => t.joint === axis.joint && t.plane === axis.plane && t.side === side,
    )
    return travel ? { ...axis, lo: travel.lo, hi: travel.hi } : { ...axis, lo: 0, hi: 0 }
  })

  const points: WorkspacePoint[] = []
  const angles = new Array(ranges.length).fill(0)
  let reach = 0
  let freeCount = 0
  const min = { x: Infinity, y: Infinity, z: Infinity }
  const max = { x: -Infinity, y: -Infinity, z: -Infinity }

  const total = Math.pow(steps, ranges.length)
  for (let index = 0; index < total; index++) {
    let rest = index
    for (let a = 0; a < ranges.length; a++) {
      const i = rest % steps
      rest = Math.floor(rest / steps)
      const range = ranges[a]
      angles[a] = steps === 1 ? 0 : range.lo + ((range.hi - range.lo) * i) / (steps - 1)
    }

    const lookup: AngleLookup = (joint, plane, s) => {
      if (s !== side) return restAngle(joint, plane, s)
      for (let a = 0; a < ranges.length; a++) {
        if (ranges[a].joint === joint && ranges[a].plane === plane) return angles[a]
      }
      return restAngle(joint, plane, s)
    }

    const pose = poseAt(skeleton, lookup, place)
    const tip = tipPosition(pose, chain.tip, side)
    const p = {
      x: tip.x - pose.pelvis.position.x,
      y: tip.y - pose.pelvis.position.y,
      z: tip.z - pose.pelvis.position.z,
    }

    const free = clearanceAt(pose, capsules, skip).distance >= CLEARANCE_MIN
    points.push({ p, free })

    if (!free) continue
    freeCount++
    reach = Math.max(reach, Math.hypot(p.x, p.y, p.z))
    min.x = Math.min(min.x, p.x)
    min.y = Math.min(min.y, p.y)
    min.z = Math.min(min.z, p.z)
    max.x = Math.max(max.x, p.x)
    max.y = Math.max(max.y, p.y)
    max.z = Math.max(max.z, p.z)
  }

  const empty = freeCount === 0
  return {
    chain,
    side,
    points,
    freeCount,
    reach,
    box: empty ? { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } } : { min, max },
    steps,
  }
}

function tipPosition(pose: Pose, tip: ChainSpec['tip'], side: Side): Vec3 {
  if (tip === 'wrist') return pose.wrist[side].position
  if (tip === 'ankle') return pose.ankle[side].position
  return pose.mtp[side].position
}

/* -------------------------------------------------------------------------
   Pose von Hand
   ------------------------------------------------------------------------- */

/**
 * Eine von Hand gestellte Pose — Winkel je Achse, in Grad.
 *
 * Flach als `joint|plane|side`, aus demselben Grund wie bei den
 * Modelländerungen: die Zahl der Einträge ist die Zahl der verstellten Achsen,
 * und eine einzelne zurückzunehmen ist ein `delete`. Was nicht darin steht,
 * steht in der Ruhelage.
 */
export type ManualPose = Record<string, number>

export const manualKey = (joint: string, plane: PlaneKey, side: Side): string =>
  `${joint}|${plane}|${side}`

/** Die Pose als Nachschlagefunktion, mit der Ruhelage als Rückfall. */
export const manualLookup =
  (pose: ManualPose): AngleLookup =>
  (joint, plane, side) =>
    pose[manualKey(joint, plane, side)] ?? restAngle(joint, plane, side)

export interface ManualCheck {
  pose: Pose
  /** Höhe der Beckenmitte über dem Boden in dieser Stellung, m. */
  pelvisHeight: number
  /**
   * Wie weit das Becken tiefer steht als in der Grundstellung, m.
   *
   * GEGEN DIE GRUNDSTELLUNG UND NICHT GEGEN `pelvisRest`. Letzteres ist die
   * Ruhehöhe der Hüftnickachse aus dem Punktemodell, und in ihr steht die Sohle
   * NICHT auf null — der Bezug wäre dann ein Versatz von 73 mm, der in jeder
   * Stellung mitliefe und in der Grundstellung als „73 mm angehoben" erschiene.
   * So ist die Grundstellung null und eine Kniebeuge positiv.
   */
  drop: number
  /** Kleinster Abstand zweier Hüllkörper, m. */
  clearance: number
  /** Welches Paar dort am engsten steht. */
  pair: string | null
  /** Achsen, deren Winkel ausserhalb ihres geltenden Bereichs liegt. */
  outside: { joint: string; plane: PlaneKey; side: Side; angle: number; lo: number; hi: number }[]
  /** Wie viele Achsen von Hand verstellt sind. */
  moved: number
}

/**
 * Stellt eine von Hand gesetzte Pose auf den Boden und prüft sie.
 *
 * AUF DEN BODEN STELLEN HEISST: den tiefsten Sohlenpunkt suchen und den ganzen
 * Körper um diesen Betrag anheben oder absenken. Ohne das steht die Figur bei
 * jeder Kniebeuge in der Luft, und die Pose sieht falsch aus, obwohl sie es
 * nicht ist — die Winkel stimmen, nur die Bezugshöhe fehlt.
 *
 * Der Freigang wird in der FERTIGEN Stellung geprüft, nicht achsweise. Das ist
 * der Unterschied zu den Achsgrenzen: dort steht alles andere in der Ruhelage,
 * hier steht alles da, wo es der Mensch hingestellt hat. Nur so findet man die
 * Kombinationen, die einzeln unauffällig sind.
 */
export function checkManual(
  model: HumanoidModel,
  skeleton: Skeleton,
  pose: ManualPose,
  /* Ohne Achsgrenzen bleibt `outside` leer. Die Bühne braucht nur die Stellung
     und den Freigang; die Achsgrenzen kosten eine eigene Suche, und sie an
     dieser Stelle zu erzwingen hiesse, sie bei jedem Reglerzug neu zu rechnen. */
  travels: AxisTravel[] = [],
): ManualCheck {
  const capsules = buildCapsules(skeleton, model.scale)
  const skip = neighbourPairs()
  const lookup = manualLookup(pose)

  const stand = (at: AngleLookup): { pose: Pose; height: number } => {
    const draft = poseAt(skeleton, at, { x: 0, y: skeleton.pelvisRest, z: 0 })
    const low = Math.min(soleHeight(skeleton, draft, 'R'), soleHeight(skeleton, draft, 'L'))
    const height = skeleton.pelvisRest - low
    return { pose: poseAt(skeleton, at, { x: 0, y: height, z: 0 }), height }
  }

  const { pose: placed, height } = stand(lookup)
  const { height: restHeight } = stand(restAngle)

  const hit = clearanceAt(placed, capsules, skip)

  const outside: ManualCheck['outside'] = []
  for (const travel of travels) {
    const angle = lookup(travel.joint, travel.plane, travel.side)
    if (angle < travel.lo - 1e-6 || angle > travel.hi + 1e-6) {
      outside.push({
        joint: travel.joint,
        plane: travel.plane,
        side: travel.side,
        angle,
        lo: travel.lo,
        hi: travel.hi,
      })
    }
  }

  return {
    pose: placed,
    pelvisHeight: height,
    drop: restHeight - height,
    clearance: hit.distance,
    pair: collision(hit),
    outside,
    moved: Object.keys(pose).length,
  }
}
